/**
 * Hook for admin facial verification configuration CRUD.
 * Uses @tanstack/react-query for caching and mutations.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

// Types
export interface TenantFacialConfig {
  id: string;
  tenantId: string;
  requireOnLogin: boolean;
  requireOnPixAboveValue: boolean;
  pixValueThreshold: number;
  requireOnWithdrawal: boolean;
  requireOnTransfer: boolean;
  requireOnProfileChange: boolean;
  livenessEnabled: boolean;
  deepfakeAnalysisEnabled: boolean;
  mapping3DEnabled: boolean;
  continuousMonitorEnabled: boolean;
  documentMatchEnabled: boolean;
  continuousIntervalMinutes: number;
  maxVerificationAttempts: number;
  verifiedSessionMinutes: number;
  masterKeyVersion: number;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateFacialConfigDto {
  requireOnLogin?: boolean;
  requireOnPixAboveValue?: boolean;
  pixValueThreshold?: number;
  requireOnWithdrawal?: boolean;
  requireOnTransfer?: boolean;
  requireOnProfileChange?: boolean;
  livenessEnabled?: boolean;
  deepfakeAnalysisEnabled?: boolean;
  mapping3DEnabled?: boolean;
  continuousMonitorEnabled?: boolean;
  documentMatchEnabled?: boolean;
  continuousIntervalMinutes?: number;
  maxVerificationAttempts?: number;
  verifiedSessionMinutes?: number;
}

export interface RotateKeyResponse {
  newVersion: number;
  message: string;
}

const BASE_URL = '/facial-verification-advanced/config';

// ==================== QUERY HOOKS ====================

/**
 * Fetch the tenant's facial verification configuration.
 */
export function useFacialConfig() {
  return useQuery<TenantFacialConfig>({
    queryKey: ['facial-config'],
    queryFn: async () => {
      const response = await api.get(BASE_URL);
      return response.data;
    },
  });
}

// ==================== MUTATION HOOKS ====================

/**
 * Update the tenant's facial verification configuration.
 */
export function useUpdateFacialConfig() {
  const queryClient = useQueryClient();

  return useMutation<TenantFacialConfig, Error, UpdateFacialConfigDto>({
    mutationFn: async (data) => {
      const response = await api.put(BASE_URL, data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facial-config'] });
    },
  });
}

/**
 * Rotate the tenant's master encryption key.
 */
export function useRotateFacialKey() {
  const queryClient = useQueryClient();

  return useMutation<RotateKeyResponse, Error, void>({
    mutationFn: async () => {
      const response = await api.post(`${BASE_URL}/rotate-key`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['facial-config'] });
    },
  });
}
